import { useState } from 'react';
import { Loader2, Star } from 'lucide-react';

import {
  useSectionFavoritePreview,
  useSectionFavorites,
} from '@/hooks/useSectionFavorites';
import type { SectionFavorite } from '@/services/sectionFavoritesService';

import FavoritePreviewFrame from './FavoritePreviewFrame';

const when = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function Preview({ favorite }: { favorite: SectionFavorite }) {
  const preview = useSectionFavoritePreview(favorite.id);

  if (preview.isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the stored source…
      </div>
    );
  }

  if (preview.isError) {
    return (
      <p className="m-4 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        This favorite cannot be shown: {(preview.error as Error).message}
      </p>
    );
  }

  if (!preview.data?.html) {
    return (
      <p className="m-4 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        The preview source for this favorite is missing on the server. Nothing
        is being shown in its place.
      </p>
    );
  }

  return (
    <FavoritePreviewFrame
      title={`Favorite: ${favorite.name}`}
      html={preview.data.html}
      className="h-[28rem] w-full rounded border border-border bg-white"
    />
  );
}

export default function FavoritesLibrary() {
  const favorites = useSectionFavorites();
  const [openId, setOpenId] = useState<string | null>(null);

  if (favorites.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading the library…
      </div>
    );
  }

  if (favorites.isError) {
    return (
      <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        Could not load the favorites library: {(favorites.error as Error).message}
      </p>
    );
  }

  const rows = favorites.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sections have been favorited yet. Open a saved page from History and
        save one from the Builder tab.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map((f) => (
        <li key={f.id} className="rounded border border-border bg-card">
          <button
            type="button"
            onClick={() => setOpenId(openId === f.id ? null : f.id)}
            aria-expanded={openId === f.id}
            className="flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-teal-light"
          >
            <Star className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{f.name}</span>
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                {f.semantic_role} · {f.source_tenant_id} · {when(f.created_at)}
                {f.created_by ? ` · ${f.created_by}` : ''}
              </span>
            </span>
          </button>
          {openId === f.id && <Preview favorite={f} />}
        </li>
      ))}
    </ul>
  );
}
